import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck, MapPin, MessageSquare, Search, ShieldAlert, ShoppingCart, Star, Timer } from "lucide-react";
import { toast } from "sonner";
import { api, fmtTL } from "../lib/api";
import { fmtQty4 } from "../lib/format";
import QualityBadge from "../components/QualityBadge";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Storefront() {
    const { supplierId } = useParams();
    const { user } = useAuth();
    const { refreshCart } = useShop();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("all");
    const [cats, setCats] = useState([]);
    const [rating, setRating] = useState(null);

    useEffect(() => {
        api.get(`/suppliers/${supplierId}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.status || true));
        api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
        api.get("/complaints/scores").then((r) => {
            const s = (r.data || []).find((x) => x.target_type === "supplier" && x.target_id === supplierId);
            if (s) setRating(s);
        }).catch(() => {});
    }, [supplierId]);

    const addToCart = async (o) => {
        try {
            await api.post("/cart/items", { offer_id: o.offer_id, qty: o.moq || 1 });
            await refreshCart();
            toast.success(`${o.product_name} sepete eklendi`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eklenemedi");
        }
    };

    if (error) {
        return (
            <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="storefront-forbidden">
                <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
                <h1 className="mt-4 font-display text-xl font-bold">Bu sayfaya erişim yetkiniz yok</h1>
                <p className="mt-2 text-sm text-muted-foreground">Tedarikçi vitrinleri yalnızca üye mağazalar tarafından görüntülenebilir.</p>
            </div>
        );
    }
    if (!data) return <div className="h-96 animate-pulse rounded-2xl bg-muted" />;

    const offers = (data.offers || []).filter((o) =>
        (!q || o.product_name.toLowerCase().includes(q.toLowerCase())) && (cat === "all" || o.category_id === cat)
    );
    const usedCats = [...new Set((data.offers || []).map((o) => o.category_id))];

    return (
        <div className="space-y-8" data-testid="storefront-page">
            <div className="overflow-hidden rounded-3xl border bg-gradient-to-br from-stone-900 to-stone-800 text-stone-100 shadow-lg">
                <div className="flex flex-wrap items-center gap-6 p-8">
                    {data.logo ? (
                        <img src={data.logo} alt={data.name} className="h-24 w-24 rounded-2xl border-2 border-white/20 object-cover" data-testid="storefront-logo" />
                    ) : (
                        <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-terra-600 to-saffron-400 font-display text-3xl font-extrabold text-white">
                            {data.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                    )}
                    <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="storefront-name">{data.name}</h1>
                            {data.verified && <Badge className="border-0 bg-blue-500/20 text-blue-300"><BadgeCheck className="mr-1 h-3 w-3" /> Doğrulanmış</Badge>}
                        </div>
                        <p className="mt-1 flex items-center gap-1.5 text-sm text-stone-400"><MapPin className="h-3.5 w-3.5" /> {data.district ? `${data.district}, ` : ""}{data.city}</p>
                        {data.description && <p className="mt-2 max-w-2xl text-sm text-stone-300">{data.description}</p>}
                        <div className="mt-3 flex gap-6 text-center">
                            <div><p className="flex items-center gap-1 font-display text-xl font-extrabold text-saffron-400"><Star className="h-4 w-4 fill-saffron-400" />{data.trust_score}</p><p className="text-[10px] uppercase tracking-wider text-stone-500">Güven Skoru</p></div>
                            <div><p className="flex items-center gap-1 font-display text-xl font-extrabold"><Timer className="h-4 w-4 text-stone-400" />%{data.on_time_rate}</p><p className="text-[10px] uppercase tracking-wider text-stone-500">Zamanında Teslim</p></div>
                            {rating && (
                                <div data-testid="storefront-buyer-rating">
                                    <p className="flex items-center gap-1 font-display text-xl font-extrabold text-amber-400"><Star className="h-4 w-4 fill-amber-400" />{rating.avg_rating.toFixed(1)}<span className="text-xs font-medium text-stone-400">({rating.total})</span></p>
                                    <p className="text-[10px] uppercase tracking-wider text-stone-500">Alıcı Puanı</p>
                                </div>
                            )}
                            <div><p className="font-display text-xl font-extrabold">{(data.offers || []).length}</p><p className="text-[10px] uppercase tracking-wider text-stone-500">Aktif Ürün</p></div>
                        </div>
                    </div>
                    {user?.role === "buyer" && (
                        <Button asChild className="bg-terra-600 text-white hover:bg-terra-700" data-testid="storefront-message">
                            <Link to={`/panel/tedarikci/${supplierId}`}><MessageSquare className="mr-2 h-4 w-4" /> İletişim / Detay</Link>
                        </Button>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2" data-testid="storefront-filters">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün ara..." className="h-9 w-56 pl-8 text-sm" data-testid="storefront-search" />
                </div>
                <button onClick={() => setCat("all")} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${cat === "all" ? "bg-primary text-primary-foreground border-primary" : ""}`} data-testid="storefront-cat-all">Tümü</button>
                {usedCats.map((cid) => (
                    <button key={cid} onClick={() => setCat(cid)} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${cat === cid ? "bg-primary text-primary-foreground border-primary" : ""}`} data-testid={`storefront-cat-${cid}`}>
                        {cats.find((c) => c.category_id === cid)?.name || cid}
                    </button>
                ))}
            </div>

            <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4" data-testid="storefront-grid">
                {offers.map((o, i) => (
                    <div key={o.offer_id} className="group animate-fade-up overflow-hidden rounded-2xl border bg-card shadow-sm transition-shadow hover:shadow-md" style={{ animationDelay: `${Math.min(i, 8) * 0.04}s` }} data-testid={`storefront-product-${o.offer_id}`}>
                        <img src={o.image} alt={o.product_name} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                        <div className="p-4">
                            <p className="font-display text-sm font-bold leading-snug">{o.product_name} <QualityBadge quality={o.quality} tid={o.offer_id} /></p>
                            <div className="mt-2 flex items-baseline gap-1.5">
                                <span className="font-display text-lg font-extrabold text-terra-600">{fmtTL(o.price)}</span>
                                <span className="text-xs text-muted-foreground">/ {o.unit} (KDV dahil)</span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">Min. {fmtQty4(o.moq, o.unit)} {o.unit} • {o.delivery_days} gün teslim • {fmtQty4(o.stock, o.unit)} stok</p>
                            {user?.role === "buyer" && (
                                <Button size="sm" className="mt-3 w-full bg-terra-600 text-white hover:bg-terra-700" onClick={() => addToCart(o)} data-testid={`storefront-add-${o.offer_id}`}>
                                    <ShoppingCart className="mr-1.5 h-3.5 w-3.5" /> Sepete Ekle
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
                {!offers.length && <p className="col-span-full rounded-2xl border border-dashed p-16 text-center text-sm text-muted-foreground">Bu filtrede ürün yok.</p>}
            </div>
        </div>
    );
}
